import { Box, Input, Textarea, Flex, Fieldset } from "@chakra-ui/react";
import { Select, chakraComponents } from "chakra-react-select";
import DateSelectController from "@/components/fields/DateSelect";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { BoardCard } from "src/api/BoardCard";
import { useEffect, useState } from "react";
import ErrorMessage from "src/components/ErrorMessage";
import { formatBoardDue, formatBoardTimestamp, parseBoardDue } from "src/data/DateTimeUtil";
import { BoardTag } from "src/api/BoardTag";
import { IdAndName } from "@/api/IdAndName";
import { Field } from "@/components/ui/field"
import { Tag } from "@/components/ui/tag";
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogCloseTrigger, DialogFooter } from "@/components/ui/dialog";
import CancelButton from "@/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { boardUserOptions } from "./BoardUsers";


export interface CardDialogProps {
    data: BoardCard | null;
    users: ReadonlyArray<IdAndName>;
    currentUserId?: number | null;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (b: BoardCard) => Promise<void>;
}

const tagOptions = [
    { value: "feature", label: "Feature", colorPalette: "green" },
    { value: "improvement", label: "Improvement", colorPalette: "orange" },
    { value: "bug", label: "Bug", colorPalette: "red" },
    { value: "todo", label: "To Do", colorPalette: "blue" },
    { value: "tech", label: "Tech", colorPalette: "purple" },
    // { value: "purple", label: "Purple", colorPalette: "cyan" },
    // { value: "yellow", label: "Yellow", colorPalette: "yellow" },
];

const customOptionItem = (props) => (
    <chakraComponents.Option label={props.data.name} {...props}>
        <Flex align="center">
            <Tag size='md' key={props.data.value} variant='subtle' colorPalette={props.data.colorPalette} fontFamily='Verdana' fontSize='xs' dropShadow='3px'>
                {props.data.label}
            </Tag>
        </Flex>
    </chakraComponents.Option>
);

const CardDialog = ({ data, users, currentUserId, isOpen, onClose, onConfirm }: CardDialogProps) => {

    const [error, setError] = useState('');
    const userOptions = boardUserOptions(users);

    const createDefault = (): BoardCard => {
        const now = new Date();
        return {
            id: crypto.randomUUID(),
            title: '',
            description: null,
            label: null,
            tags: [],
            createdBy: currentUserId ?? null,
            assignedTo: currentUserId ?? null,
            createdAt: now,
            updatedAt: now,
        }
    }

    const { control, register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm<BoardCard>({
        defaultValues: createDefault()
    });
    const createdAt = watch('createdAt');

    const onSubmitInternal: SubmitHandler<BoardCard> = (p) => {
        console.info(`submitting: ${JSON.stringify(p)}`);
        return onConfirm(p).then(() => {
            reset();
            onClose()
        }).catch(err => setError('Fehler'));
    };

    // update the state got from the props at every open
    useEffect(() => {
        console.info(`resetting form to ${JSON.stringify(data)}`)
        data ? reset(data) : reset(createDefault());
    }, [data, reset, isOpen, currentUserId]);

    return (
        <DialogRoot
            open={isOpen}
            onOpenChange={() => onClose()}
            placement='center'
            size='lg'
            motionPreset='scale'
            scrollBehavior='outside'
        >
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmitInternal)}>
                    <DialogHeader>{data ? 'Update' : 'Create'} a task</DialogHeader>
                    <DialogCloseTrigger />
                    <DialogBody pb={6}>
                        <Fieldset.Root>
                            {error && <ErrorMessage message={error} />}
                            <Field label='Task to do' required>
                                <Input autoFocus={true} placeholder='To Do' {...register('title')} />
                            </Field>

                            <Field label='Description' mt={4}>
                                <Textarea placeholder='More details ...' {...register('description')} height='12rem' />
                            </Field>

                            <Controller control={control} name='tags'
                                render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error } }) => (
                                    <Field label='Tag' errorText={error?.message}>
                                        <Select
                                            isMulti
                                            options={tagOptions}
                                            placeholder="Select tags..."
                                            closeMenuOnSelect={false}
                                            components={{ Option: customOptionItem }}
                                            //formatOptionLabel={(option: Option) => customSelectItem(option)}
                                            ref={ref}
                                            name={name}
                                            onBlur={onBlur}
                                            onChange={(val: any[]) => {
                                                let ops = Array.from(val.values()).map(v => {
                                                    let tag: BoardTag = {
                                                        title: v.label,
                                                        bgcolor: v.colorPalette,
                                                        color: 'white'
                                                    }
                                                    return tag;
                                                });
                                                onChange(ops);
                                            }}
                                            value={tagOptions.filter(c => value.find(t => t.title === c.label))}
                                        />
                                    </Field>
                                )} />

                            <Flex gap={4} mt={4} direction={{ base: 'column', md: 'row' }}>
                                <Controller control={control} name='assignedTo'
                                    render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error } }) => (
                                        <Field label='Assigned to' flex='1' errorText={error?.message}>
                                            <Select
                                                options={userOptions}
                                                placeholder="Unassigned"
                                                isClearable
                                                ref={ref}
                                                name={name}
                                                onBlur={onBlur}
                                                onChange={(val: any) => onChange(val?.value ?? null)}
                                                value={userOptions.find(option => option.value === value) ?? null}
                                            />
                                        </Field>
                                    )} />

                                <Box flex='1'>
                                    <DateSelectController
                                        control={control}
                                        name='label'
                                        label='Due'
                                        clearable
                                        w='full'
                                        placeholderText='Set due date if available'
                                        parseValue={(value) => parseBoardDue(typeof value === 'string' ? value : null)}
                                        serializeValue={(date) => date ? formatBoardDue(date) : null}
                                    />
                                </Box>
                            </Flex>
                            <Flex gap={4} mt={4} direction={{ base: 'column', md: 'row' }}>
                                <Controller control={control} name='createdBy'
                                    render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error } }) => (
                                        <Field label='Created by' flex='1' errorText={error?.message}>
                                            <Select
                                                options={userOptions}
                                                placeholder="Unknown"
                                                isClearable
                                                ref={ref}
                                                name={name}
                                                onBlur={onBlur}
                                                onChange={(val: any) => onChange(val?.value ?? null)}
                                                value={userOptions.find(option => option.value === value) ?? null}
                                            />
                                        </Field>
                                    )} />
                                <Field label='Created at' flex='1'>
                                    <Input readOnly value={formatBoardTimestamp(createdAt)} />
                                </Field>
                            </Flex>
                        </Fieldset.Root>
                    </DialogBody>

                    <DialogFooter>
                        <CancelButton onClick={onClose}/>
                        <SubmitButton isSubmitting={isSubmitting} /> 
                    </DialogFooter>
                </form>
            </DialogContent>
        </DialogRoot>
    )
}

export default CardDialog
